import React, {useState, useRef, useCallback, useEffect} from "react";
import Message from "./Message";
import ChatForm from "./ChatForm";
import {AssistantGreeting} from "./AssistantGreeting";
import {removeLocalStorageItem} from "src/utilities/cookie";
import Icon from "../Icon";
import {AssistantSettings, WidgetSettings} from "src/interfaces/settings";
import {t} from "i18next";
import LoadingDots from "./LoadingDots";
import {KnowledgeBase} from "src/interfaces/knowledgeBase";
import {useThread, useMessages, useAssistantChat} from "../../utilities/hooks/useAssistantChat";
import FeedbackSection from "./FeedbackSection";
import {WidgetContent} from "../widget/Widget";
import {AssistantChannel, ChatAssistantMetadata} from "src/interfaces/assistant";
import EmailInput from "./EmailInput";
import {AlertError} from "src/reducers/store";

interface AssistantChatProps {
  currentKnowledgeBase: KnowledgeBase;
  widgetSettings?: WidgetSettings;
  heading?: string;
  greeting?: string;
  assistantSettings?: AssistantSettings;
  channel: AssistantChannel;
  requireEmail?: boolean;
  showFeedbackSection?: boolean;
  showClearChatButton?: boolean;
  question?: string;
  language: string;
  navigateTo?: (content: WidgetContent) => void;
  clearSuggestedQuestion?: () => void;
}

const AssistantChat = (props: AssistantChatProps) => {
  const assistant = props.assistantSettings?.assistants?.find((assistant) => assistant.channel === props.channel);
  const metaData: ChatAssistantMetadata = assistant?.metadata || null;

  const [validatedEmail, setValidatedEmail] = useState<string>("");
  const {thread, initializeThread, validateEmail, useUpdateThreadStorage} = useThread(
    props.currentKnowledgeBase.id,
    props.assistantSettings,
    metaData?.maxThreadAge,
    props.channel,
  );
  const {messages, setMessages, isLoading, sendMessage} = useMessages(
    props.currentKnowledgeBase.id,
    thread,
    assistant?.id,
    props.channel,
  );
  const {useScrollToBottom, useExpandFeedbackSection, endConversation} = useAssistantChat();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const handleExternalQuestion = useCallback(
    async (question: string) => {
      const assistantId = props.assistantSettings?.assistants.find(
        (assistant) => assistant.channel === props.channel,
      )?.id;
      await sendMessage(question, assistantId);
    },
    [props.assistantSettings, props.channel, sendMessage],
  );

  const handleValidEmail = (email: string) => {
    setValidatedEmail(email);
    validateEmail();
  };

  const handleSendMessage = async (message: string) => {
    const assistantId = props.assistantSettings?.assistants.find(
      (assistant) => assistant.channel === props.channel,
    )?.id;
    if (!assistantId) {
      console.error(`No Assistant found for channel: ${props.channel}`);
      AlertError(`No Assistant found for channel: ${props.channel}`);
      return;
    }

    await sendMessage(message, assistantId);
    props.clearSuggestedQuestion?.();
  };

  const clearChat = () => {
    const assistantId = props.assistantSettings?.assistants.find(
      (assistant) => assistant.channel === props.channel,
    )?.id;
    endConversation(messages, props.channel, props.currentKnowledgeBase.id, assistantId, thread?.threadId);
    removeLocalStorageItem(`assistantThread-${props.channel}-${props.currentKnowledgeBase.id}`);
    setMessages([]);
    initializeThread();
  };

  useEffect(() => {
    if (props.question) {
      handleExternalQuestion(props.question);
    }
  }, [props.question, handleExternalQuestion]);

  useExpandFeedbackSection(messages, setIsCollapsed);
  useScrollToBottom(chatContainerRef, [messages]);
  useUpdateThreadStorage(validatedEmail, props.channel, props.currentKnowledgeBase.id);

  return (
    <div className="flex flex-col h-full w-full">
      {props.heading && <h2 className="text-lg font-semibold  m-4">{props.heading}</h2>}
      {props.channel === AssistantChannel.CHAT && props.requireEmail && !thread?.hasValidatedEmail ? (
        <EmailInput onValidEmail={handleValidEmail} widgetSettings={props.widgetSettings} language={props.language} />
      ) : (
        <>
          <div className="flex-1 overflow-y-auto p-4 pb-8 relative h-full" ref={chatContainerRef}>
            <AssistantGreeting widgetSettings={props.widgetSettings} metaData={metaData} greeting={props.greeting} />
            {props.channel === AssistantChannel.CHAT && (
              <Message
                metaData={metaData}
                text={metaData?.initialMessage || t("ASSISTANT_CHAT_INITIAL_MESSAGE")}
                sender="assistant"
                channel={props.channel}
              />
            )}
            {messages
              .map((message, index) => (
                <Message
                  key={index}
                  metaData={metaData}
                  text={message.text}
                  sender={message.sender}
                  channel={props.channel}
                />
              ))
              .reverse()}
          </div>
          {messages.length >= 2 && props.showFeedbackSection && props.channel === AssistantChannel.CHAT && (
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="h-8 w-8 flex items-center justify-center absolute bottom-14 left-1/2 transform -translate-x-1/2 border rounded-full bg-white z-10"
            >
              <Icon name={isCollapsed ? "chevron-up" : "chevron-down"} />
            </button>
          )}
          {!isCollapsed &&
            props.showFeedbackSection &&
            messages.length >= 2 &&
            props.channel === AssistantChannel.CHAT && (
              <FeedbackSection
                knowledgeBaseId={props.currentKnowledgeBase.id}
                assistant={props.assistantSettings?.assistants.find(
                  (assistant) => assistant.channel === AssistantChannel.CHAT,
                )}
                navigateTo={props.navigateTo}
                messages={messages}
                widgetSettings={props.widgetSettings}
                threadId={thread?.threadId}
                metaData={metaData}
              />
            )}
          <div className="relative">
            {isLoading && (
              <div
                className={`absolute left-4 ${
                  isCollapsed || !props.showFeedbackSection || props.channel !== AssistantChannel.CHAT
                    ? "bottom-20"
                    : "bottom-[17.2rem]"
                }`}
              >
                <LoadingDots />
              </div>
            )}
            {(props.showClearChatButton || props.channel === AssistantChannel.AGENT) && (
              <button
                onClick={clearChat}
                title={t("CLEAR_CHAT")}
                className="absolute right-4 bottom-20 z-10 p-2 h-6 w-6 flex items-center justify-center border rounded-full bg-card"
              >
                <Icon name="x" iconSize="16px" />
              </button>
            )}
            <ChatForm onSendMessage={handleSendMessage} isLoading={isLoading} />
          </div>
        </>
      )}
    </div>
  );
};

export default AssistantChat;
